import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  FaCloudUploadAlt,
  FaFileAlt,
  FaMagic,
  FaPen,
  FaCheckCircle,
} from "react-icons/fa";
import {
  GetUser,
  parseResumeFile,
  getAuthConfig,
} from "../../../../Redux/Slices/User";
import { axiosInstance } from "../../../../Axios/AxiosInstance";
import Location from "../../../Admin/Location";
import { needsJobseekerOnboarding } from "../../../../Helper/jobseekerRedirect";
import "../JobSeekerProfile/JobseekerProfileDashboard.css";
import "./JobseekerOnboarding.css";

const STEPS = ["welcome", "upload", "review", "manual", "done"];

const PARSE_STAGES = [
  { text: "Uploading your resume securely...", tip: "Your file stays private and encrypted." },
  { text: "Reading PDF content...", tip: "We extract text from every page of your resume." },
  { text: "AI is analysing your experience...", tip: "Gemini maps roles, skills, and qualifications." },
  { text: "Matching CA/CS/CMA skills...", tip: "We align your profile with top finance roles." },
  { text: "Building your profile draft...", tip: "Almost there — you'll review before saving." },
  { text: "Polishing the details...", tip: "Good profiles get noticed by employers faster." },
];

const emptyDraft = () => ({
  name: "",
  email: "",
  mobile: "",
  linkedin: "",
  experience_level: "Fresher",
  total_experience: "",
  career: { profile_headline: "", skills: [], preferred_location: [] },
  address: { city: "", state: "", country: "India" },
  experience: [],
  academic: [],
  prof_qualification: [],
  languages: [],
});

const JobseekerOnboarding = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userId = localStorage.getItem("id");
  const { userData, completionPercentage } = useSelector((state) => state?.User);

  const [step, setStep] = useState("welcome");
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resumePath, setResumePath] = useState("");
  const [parseSource, setParseSource] = useState("manual");
  const [draft, setDraft] = useState(emptyDraft());
  const [locationData, setLocationData] = useState({
    Country: "India",
    State: "",
    Cities: "",
  });
  const [completionPct, setCompletionPct] = useState(0);
  const [parseStageIndex, setParseStageIndex] = useState(0);

  useEffect(() => {
    document.title = "Complete Profile | CAJobPortal";
    if (userId) dispatch(GetUser(userId));
  }, [dispatch, userId]);

  useEffect(() => {
    if (userData && !needsJobseekerOnboarding(userData, completionPercentage)) {
      navigate("/jobs", { replace: true });
    }
  }, [userData, completionPercentage, navigate]);

  useEffect(() => {
    if (userData) {
      setDraft((prev) => ({
        ...prev,
        name: userData.name || prev.name,
        email: userData.email || prev.email,
        mobile: userData.mobile || prev.mobile,
        experience_level: userData.experience_level || prev.experience_level,
      }));
    }
  }, [userData]);

  const stepIndex = useMemo(() => STEPS.indexOf(step), [step]);

  useEffect(() => {
    if (!parsing) {
      setParseStageIndex(0);
      return undefined;
    }
    const timer = setInterval(() => {
      setParseStageIndex((prev) => (prev + 1) % PARSE_STAGES.length);
    }, 2200);
    return () => clearInterval(timer);
  }, [parsing]);

  const validatePdf = useCallback((selected) => {
    if (!selected) return false;
    if (selected.type !== "application/pdf") {
      toast.error("Please upload a PDF resume");
      return false;
    }
    if (selected.size > 5 * 1024 * 1024) {
      toast.error("Resume must be smaller than 5MB");
      return false;
    }
    return true;
  }, []);

  const handleFileSelect = useCallback(
    (selected) => {
      if (!validatePdf(selected)) return;
      setFile(selected);
    },
    [validatePdf]
  );

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      const dropped = e.dataTransfer.files?.[0];
      handleFileSelect(dropped);
    },
    [handleFileSelect]
  );

  const isEstablishedUser =
    userData && !needsJobseekerOnboarding(userData, completionPercentage);

  if (userId && !userData) {
    return (
      <div className="jsp-page onboarding-page">
        <div className="jsp-shell onboarding-shell">
          <section className="onboarding-card" style={{ textAlign: "center" }}>
            <p className="onboarding-desc">Loading your profile...</p>
          </section>
        </div>
      </div>
    );
  }

  if (isEstablishedUser) {
    return (
      <div className="jsp-page onboarding-page">
        <div className="jsp-shell onboarding-shell">
          <section className="onboarding-card" style={{ textAlign: "center" }}>
            <p className="onboarding-desc">Your profile is ready. Taking you to jobs...</p>
          </section>
        </div>
      </div>
    );
  }

  const handleParseResume = async () => {
    if (!file) {
      toast.error("Select a resume PDF first");
      return;
    }
    setParsing(true);
    try {
      const result = await parseResumeFile(file);
      if (result?.success) {
        setDraft((prev) => ({ ...prev, ...(result.draft || {}) }));
        setResumePath(result.resumePath || "");
        setParseSource(result.source || "gemini");
        setStep("review");
        const viaLabel =
          result.source === "gemini" ? "AI" : "smart extraction";
        toast.success(`Resume parsed via ${viaLabel} — review your details`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not parse resume");
    } finally {
      setParsing(false);
    }
  };

  const handleManualOnlySave = async () => {
    if (!draft.name || !draft.mobile) {
      toast.error("Name and mobile are required");
      return;
    }
    if (!resumePath) {
      toast.error("Upload your resume to finish onboarding and apply to jobs");
      setStep("upload");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...draft,
        resume_parse_source: "manual",
        address: {
          country: locationData.Country || draft.address?.country || "India",
          state: locationData.State || draft.address?.state || "",
          city: Array.isArray(locationData.Cities)
            ? locationData.Cities[0]
            : locationData.Cities || draft.address?.city || "",
        },
      };
      const res = await axiosInstance.post(
        "users/onboarding/complete",
        payload,
        getAuthConfig()
      );
      if (res?.data?.success) {
        setCompletionPct(res.data.completionPercentage || 0);
        dispatch(GetUser(userId));
        setStep("done");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    if (!draft.name || !draft.mobile) {
      toast.error("Name and mobile are required");
      return;
    }
    if (!resumePath) {
      toast.error("Resume is required to complete onboarding");
      setStep("upload");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...draft,
        resume: resumePath || undefined,
        resume_parse_source: parseSource,
        address: {
          country: locationData.Country || draft.address?.country || "India",
          state: locationData.State || draft.address?.state || "",
          city: Array.isArray(locationData.Cities)
            ? locationData.Cities[0]
            : locationData.Cities || draft.address?.city || "",
        },
      };
      const res = await axiosInstance.post(
        "users/onboarding/complete",
        payload,
        getAuthConfig()
      );
      if (res?.data?.success) {
        setCompletionPct(res.data.completionPercentage || 0);
        dispatch(GetUser(userId));
        setStep("done");
        toast.success("Profile saved!");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="jsp-page onboarding-page">
      <div className="jsp-shell onboarding-shell">
        <div className="onboarding-progress">
          {["Start", "Resume", "Review", "Done"].map((label, idx) => (
            <div
              key={label}
              className={`onboarding-progress-step ${
                idx <= Math.min(stepIndex, 3) ? "is-active" : ""
              }`}
            >
              <span>{idx + 1}</span>
              <small>{label}</small>
            </div>
          ))}
        </div>

        {step === "welcome" && (
          <section className="onboarding-card">
            <p className="jsp-greeting">Welcome to CAJobPortal</p>
            <h1 className="jsp-title">Get job-ready in 2 minutes</h1>
            <p className="jsp-subtitle">
              Upload your resume and we&apos;ll auto-fill your profile. You can always edit
              details later.
            </p>
            <div className="onboarding-choice-grid">
              <button
                type="button"
                className="onboarding-choice is-primary"
                onClick={() => setStep("upload")}
              >
                <FaMagic />
                <strong>Upload resume</strong>
                <span>Recommended — AI auto-fill</span>
              </button>
              <button
                type="button"
                className="onboarding-choice"
                onClick={() => setStep("manual")}
              >
                <FaPen />
                <strong>Fill manually</strong>
                <span>Enter details step by step</span>
              </button>
            </div>
          </section>
        )}

        {step === "upload" && (
          <section className="onboarding-card onboarding-upload-card">
            <h2 className="onboarding-title">Upload your resume</h2>
            <p className="onboarding-desc">PDF only, up to 5MB</p>
            <div
              className={`onboarding-dropzone ${parsing ? "is-parsing" : ""}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
            >
              <FaCloudUploadAlt size={36} />
              <p>Drag & drop your resume here</p>
              <label className={`jsp-btn jsp-btn-primary ${parsing ? "is-disabled" : ""}`}>
                Choose PDF
                <input
                  type="file"
                  accept="application/pdf"
                  hidden
                  disabled={parsing}
                  onChange={(e) => handleFileSelect(e.target.files?.[0])}
                />
              </label>
              {file && (
                <div className="onboarding-file-pill">
                  <FaFileAlt /> {file.name}
                </div>
              )}
            </div>

            {parsing && (
              <div className="onboarding-parse-overlay" role="status" aria-live="polite">
                <div className="onboarding-parse-spinner" aria-hidden="true">
                  <span className="onboarding-parse-ring" />
                  <FaMagic className="onboarding-parse-icon" />
                </div>
                <p className="onboarding-parse-title">
                  {PARSE_STAGES[parseStageIndex].text}
                </p>
                <p className="onboarding-parse-tip">
                  {PARSE_STAGES[parseStageIndex].tip}
                </p>
                <div className="onboarding-parse-progress">
                  <div
                    className="onboarding-parse-progress-bar"
                    style={{
                      width: `${((parseStageIndex + 1) / PARSE_STAGES.length) * 100}%`,
                    }}
                  />
                </div>
                <p className="onboarding-parse-note">
                  This usually takes 10–20 seconds. Please don&apos;t close this tab.
                </p>
              </div>
            )}

            <div className="onboarding-actions">
              <button
                type="button"
                className="jsp-btn jsp-btn-ghost"
                disabled={parsing}
                onClick={() => setStep("welcome")}
              >
                Back
              </button>
              <button
                type="button"
                className="jsp-btn jsp-btn-primary"
                disabled={!file || parsing}
                onClick={handleParseResume}
              >
                {parsing ? "Analysing resume..." : "Parse & continue"}
              </button>
            </div>
          </section>
        )}

        {step === "review" && (
          <section className="onboarding-card">
            <h2 className="onboarding-title">Review your profile</h2>
            <p className="onboarding-desc">
              Parsed via {parseSource === "gemini" ? "AI" : "smart extraction"}. Edit anything
              before saving.
            </p>
            <div className="onboarding-form-grid">
              <label>
                Full name
                <input
                  value={draft.name || ""}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
              </label>
              <label>
                Mobile
                <input
                  value={draft.mobile || ""}
                  onChange={(e) => setDraft({ ...draft, mobile: e.target.value })}
                />
              </label>
              <label>
                Email
                <input
                  value={draft.email || ""}
                  onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                />
              </label>
              <label>
                Experience level
                <select
                  value={draft.experience_level || "Fresher"}
                  onChange={(e) =>
                    setDraft({ ...draft, experience_level: e.target.value })
                  }
                >
                  <option value="Fresher">Fresher</option>
                  <option value="Experienced">Experienced</option>
                </select>
              </label>
              <label className="onboarding-span-2">
                Profile headline
                <input
                  value={draft.career?.profile_headline || ""}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      career: { ...draft.career, profile_headline: e.target.value },
                    })
                  }
                />
              </label>
              <label className="onboarding-span-2">
                Skills (comma separated)
                <input
                  value={(draft.career?.skills || []).join(", ")}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      career: {
                        ...draft.career,
                        skills: e.target.value
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean),
                      },
                    })
                  }
                />
              </label>
            </div>
            <Location sendDataToParent={setLocationData} />
            <div className="onboarding-actions">
              <button
                type="button"
                className="jsp-btn jsp-btn-ghost"
                onClick={() => setStep("upload")}
              >
                Back
              </button>
              <button
                type="button"
                className="jsp-btn jsp-btn-primary"
                disabled={saving}
                onClick={handleComplete}
              >
                {saving ? "Saving..." : "Save & finish"}
              </button>
            </div>
          </section>
        )}

        {step === "manual" && (
          <section className="onboarding-card">
            <h2 className="onboarding-title">Tell us about yourself</h2>
            <div className="onboarding-form-grid">
              <label>
                Full name *
                <input
                  value={draft.name || ""}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
              </label>
              <label>
                Mobile *
                <input
                  value={draft.mobile || ""}
                  onChange={(e) => setDraft({ ...draft, mobile: e.target.value })}
                />
              </label>
              <label>
                Experience level
                <select
                  value={draft.experience_level || "Fresher"}
                  onChange={(e) =>
                    setDraft({ ...draft, experience_level: e.target.value })
                  }
                >
                  <option value="Fresher">Fresher</option>
                  <option value="Experienced">Experienced</option>
                </select>
              </label>
              <label className="onboarding-span-2">
                Profile headline
                <input
                  value={draft.career?.profile_headline || ""}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      career: { ...draft.career, profile_headline: e.target.value },
                    })
                  }
                  placeholder="e.g. CA Inter | Articleship | GST & Audit"
                />
              </label>
            </div>
            <Location sendDataToParent={setLocationData} />
            <div className="onboarding-actions">
              <button
                type="button"
                className="jsp-btn jsp-btn-ghost"
                onClick={() => setStep("welcome")}
              >
                Back
              </button>
              <button
                type="button"
                className="jsp-btn jsp-btn-secondary"
                onClick={() => setStep("upload")}
              >
                Upload resume instead
              </button>
              <button
                type="button"
                className="jsp-btn jsp-btn-primary"
                disabled={saving}
                onClick={handleManualOnlySave}
              >
                {saving ? "Saving..." : "Continue"}
              </button>
            </div>
          </section>
        )}

        {step === "done" && (
          <section className="onboarding-card onboarding-done">
            <FaCheckCircle className="onboarding-done-icon" />
            <h2 className="onboarding-title">You&apos;re all set!</h2>
            <p className="onboarding-desc">
              Profile completion: <strong>{completionPct}%</strong>. You can add more details
              anytime from your profile.
            </p>
            <div className="onboarding-actions onboarding-actions-center">
              <button
                type="button"
                className="jsp-btn jsp-btn-primary"
                onClick={() => navigate("/jobs")}
              >
                Browse jobs
              </button>
              <button
                type="button"
                className="jsp-btn jsp-btn-secondary"
                onClick={() => navigate("/jobseeker/profile/details")}
              >
                Complete profile
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default JobseekerOnboarding;
